import { Countersong } from '../Countersong.js';
import { FUSED_WEAPONS } from '../../../../../config/constants.js';

/**
 * Antiphon (Harmonics fusion: Countersong + Arc Needle) — call and response.
 *
 * Extends the Countersong rather than reimplementing it, because the arc is
 * the identity: `covers`/`reflectReady`/`noteReflect`/`reflectDamage` are what
 * Game._convertBossBullets duck-types against, and the fusion has to keep
 * turning bullets around or it isn't this weapon. What Arc Needle adds is the
 * answer: the arc's own voice stops being three straight shots and becomes a
 * volley of homing needles, and the reflect cadence tightens so the response
 * keeps up with the call.
 *
 * A note for anyone re-tuning it against dpsBench.js: the bench has no boss
 * bullets, so neither this weapon's reflect nor its Countersong parent's is
 * measured there. The ratio the balance test asserts is the *shootable* half
 * of both sides, which is the honest comparison — but it means the Antiphon in
 * a real bullet-dense fight is worth meaningfully more than the bench says,
 * and its listed shot damage is deliberately restrained for that reason.
 */
export class Antiphon extends Countersong {
  constructor() {
    super(FUSED_WEAPONS.antiphon);
  }

  /**
   * Needle count is FLAT. Everything this weapon gains per level goes into the
   * two numbers the bench can see (shot damage) and the two it can't (reflect
   * damage, and the arc's reach and width via the inherited accessors) — a
   * fourth and fifth needle on top of that put it well past the Harmonics
   * ratio, since a volley multiplies rather than adds.
   */
  get needleCount() {
    return this.config.needles;
  }

  _shotDamage() {
    return this.config.damage + this.level * this.config.damagePerLevel;
  }

  fire(origin, target, pool) {
    const angle = this.arcAngle;
    const count = this.needleCount;
    const damage = this._shotDamage();
    const turnRate = this.config.needleTurnRate + this.level * 0.2;
    for (let i = 0; i < count; i++) {
      // Spread across the sweep the arc is already holding, so the volley
      // leaves along the same line the reflects come back down.
      const t = count === 1 ? 0 : (i / (count - 1)) * 2 - 1;
      const shotAngle = angle + t * (this.arcWidth / 2);
      pool.fire({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(shotAngle) * this.config.projectileSpeed,
        vy: Math.sin(shotAngle) * this.config.projectileSpeed,
        radius: this.config.projectileRadius,
        damage,
        hits: 1,
        lifetime: this.config.lifetime,
        color: this.config.color,
        homing: !!target,
        turnRate,
        target,
      });
    }
  }
}
